import Helper from '@/utils/helper';

/**
 * Money maths for document line items (quotes, invoices, purchase orders).
 *
 * Every amount displays at 2 decimals, so every amount is CALCULATED at 2 decimals.
 * The rule that makes that possible without losing a cent:
 *
 *   Calculate in the direction the price was entered, and let the opposite side
 *   be the REMAINDER - never recompute both sides independently.
 *
 * Enter an exclusive price and the inclusive amount is derived; enter an inclusive
 * price and the exclusive amount is derived. Either way tax is the difference, so
 * value + tax == consideration always holds.
 *
 * This mirrors the VAT Act (Act 89 of 1991) s10(2): the value of a supply is the
 * consideration "less so much of such value as represents tax", where that portion
 * is "an amount equal to the tax fraction of that consideration" (15/115 at 15%).
 * The exclusive amount is legally a subtraction, not an independent multiplication.
 *
 * Deriving both sides by multiplication instead makes round inclusive prices
 * unreachable - at 15%, R80.00 has no 2-decimal exclusive price that maps back to it.
 */

/** Round a money AMOUNT to the 2 decimals it is displayed at. Nullish becomes 0. */
export const roundPrice = (value: any): number => Helper.roundToTwo(+value || 0) ?? 0;

/**
 * Round a unit PRICE for storage. Prices display at 2 decimals, but a price derived
 * by removing tax needs more precision than that to survive being redisplayed:
 * at 15% no 2-decimal exclusive price maps back to an entered R80.00 inclusive
 * (69.56 -> 79.99, 69.57 -> 80.01). The database stores 4 decimals for this reason.
 *
 * Only ever used for DERIVED prices. A price the user typed, or one seeded from an
 * inventory item, is rounded to 2 decimals at that boundary so that
 * displayed price x quantity always reconciles with the displayed line total.
 */
export const roundUnitPrice = (value: any): number => {
  const num = +value || 0;
  return Math.round((num + Number.EPSILON) * 10000) / 10000;
};

const taxRate = (taxPercentage: any): number => (+taxPercentage || 0) / 100;

export interface LineTotalInput {
  quantity: any;
  unitPrice: any;
  /** Per-line discount, 0-100. */
  discountPercentage?: any;
}

/**
 * Line total from the unit price as entered and displayed.
 * Tax-direction agnostic: pass exclusive prices to get an exclusive total,
 * inclusive prices to get an inclusive total.
 */
export const calculateLineTotal = ({ quantity, unitPrice, discountPercentage = 0 }: LineTotalInput): number => {
  const qty = +quantity || 0;
  if (!(qty > 0)) return 0;

  const subTotal = qty * (+unitPrice || 0);
  const discount = subTotal * ((+discountPercentage || 0) / 100);
  return roundPrice(subTotal - discount);
};

export interface LineTotalInclusiveInput extends LineTotalInput {
  taxPercentage: any;
}

/**
 * Inclusive line total, calculated from the SAME source as the exclusive one -
 * the unit price and quantity - and rounded once.
 *
 * Never rebuild it from an already-rounded exclusive line total: at 15% an entered
 * R80.00 stores a 69.57 exclusive line, and 69.57 x 1.15 redisplays as R80.01.
 */
export const calculateLineTotalInclusive = ({
  quantity,
  unitPrice,
  discountPercentage = 0,
  taxPercentage,
}: LineTotalInclusiveInput): number => {
  const qty = +quantity || 0;
  if (!(qty > 0)) return 0;

  const subTotal = qty * (+unitPrice || 0);
  const discount = subTotal * ((+discountPercentage || 0) / 100);
  return roundPrice((subTotal - discount) * (1 + taxRate(taxPercentage)));
};

/**
 * The tax within a tax-inclusive amount - the "tax fraction" of s10(2),
 * ie 15/115 of the consideration at 15%.
 */
export const calculateTaxFromInclusive = (inclusiveAmount: any, taxPercentage: any): number => {
  const rate = taxRate(taxPercentage);
  if (!rate) return 0;
  return roundPrice((+inclusiveAmount || 0) * (rate / (1 + rate)));
};

/** Add tax to an exclusive amount. Use when the EXCLUSIVE price was the one entered. */
export const calculateInclusive = (exclusiveAmount: any, taxPercentage: any): number =>
  roundPrice((+exclusiveAmount || 0) * (1 + taxRate(taxPercentage)));

/**
 * The exclusive unit price to STORE for an entered inclusive unit price.
 * Kept at storage precision so the entered price redisplays unchanged.
 */
export const calculateExclusiveUnitPrice = (inclusiveUnitPrice: any, taxPercentage: any): number =>
  roundUnitPrice((+inclusiveUnitPrice || 0) / (1 + taxRate(taxPercentage)));

/**
 * Strip tax from an inclusive amount. Use when the INCLUSIVE price was the one entered.
 * Computed as consideration less the tax fraction, so value + tax == consideration exactly.
 */
export const calculateExclusive = (inclusiveAmount: any, taxPercentage: any): number => {
  const inclusive = roundPrice(inclusiveAmount);
  return roundPrice(inclusive - calculateTaxFromInclusive(inclusive, taxPercentage));
};

/**
 * Tax on a line, as the difference between the two sides rather than a second
 * multiplication. Guarantees the totals block adds up.
 */
export const calculateTax = (exclusiveAmount: any, inclusiveAmount: any): number =>
  roundPrice(roundPrice(inclusiveAmount) - roundPrice(exclusiveAmount));

/** Markup as a percentage of cost, to 2 decimals. Returns '' when cost is unusable. */
export const calculateMarkup = (sellPrice: any, costPrice: any): number | '' => {
  const sell = roundPrice(sellPrice);
  const cost = roundPrice(costPrice);
  if (!cost) return '';
  if (sell === cost) return 0;
  return Math.round((sell / cost - 1) * 10000) / 100;
};

/** Sell price implied by a cost price and a markup percentage. */
export const calculatePriceFromMarkup = (costPrice: any, markupPercentage: any): number =>
  roundPrice(roundPrice(costPrice) * (1 + ((+markupPercentage || 0) / 100)));

export interface LineProfitInput {
  quantity: any;
  unitPrice: any;
  costPrice: any;
  /** Per-line discount, 0-100. */
  discountPercentage?: any;
  /** Document-wide discount, 0-100. Omit where it does not apply. */
  overallDiscountPercentage?: any;
}

/** Line profit: revenue after both discounts, less cost. */
export const calculateLineProfit = ({
  quantity,
  unitPrice,
  costPrice,
  discountPercentage = 0,
  overallDiscountPercentage = 0,
}: LineProfitInput): number => {
  const qty = +quantity || 0;
  const lineExcl = calculateLineTotal({ quantity: qty, unitPrice, discountPercentage });
  const afterOverallDiscount = roundPrice(lineExcl * (1 - ((+overallDiscountPercentage || 0) / 100)));
  const lineCost = roundPrice(qty * roundPrice(costPrice));
  return roundPrice(afterOverallDiscount - lineCost);
};

export default {
  roundPrice,
  roundUnitPrice,
  calculateLineTotal,
  calculateLineTotalInclusive,
  calculateTaxFromInclusive,
  calculateInclusive,
  calculateExclusive,
  calculateExclusiveUnitPrice,
  calculateTax,
  calculateMarkup,
  calculatePriceFromMarkup,
  calculateLineProfit,
};
